const YEAR_NUMERIC = 'YYYY';
const YEAR_2DIGIT = 'YY';
const MONTH_NUMERIC = 'M';
const MONTH_2DIGIT = 'MM';
const DAY_NUMERIC = 'D';
const DAY_2DIGIT = 'DD';

const YEAR_NUMERIC_PLACEHOLDER = 'aaaa';
const YEAR_2DIGIT_PLACEHOLDER = 'aa';
const MONTH_NUMERIC_PLACEHOLDER = 'l';
const MONTH_2DIGIT_PLACEHOLDER = 'll';
const DAY_NUMERIC_PLACEHOLDER = 'z';
const DAY_2DIGIT_PLACEHOLDER = 'zz';

const FORMAT_2DIGIT = '2-digit';
const FORMAT_NUMERIC = 'numeric';
const YMD_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const DEFAULT_SEPARATOR = '.';

const isValidDate = (date) =>
  date instanceof Date && !isNaN(date.getTime());

export function buildDisplayFormat(formatOptions = {}) {
  const {
    year = FORMAT_2DIGIT,
    month = FORMAT_2DIGIT,
    day = FORMAT_2DIGIT,
    separator = DEFAULT_SEPARATOR,
  } = formatOptions || {};

  const yearToken = year === FORMAT_NUMERIC ? YEAR_NUMERIC : YEAR_2DIGIT;
  const monthToken = month === FORMAT_2DIGIT ? MONTH_2DIGIT : MONTH_NUMERIC;
  const dayToken = day === FORMAT_2DIGIT ? DAY_2DIGIT : DAY_NUMERIC;

  return `${dayToken}${separator}${monthToken}${separator}${yearToken}`;
}

export function buildDisplayFormatForPlaceholder(formatOptions = {}) {
  const {
    year = FORMAT_2DIGIT,
    month = FORMAT_2DIGIT,
    day = FORMAT_2DIGIT,
    separator = DEFAULT_SEPARATOR,
  } = formatOptions || {};

  const yearToken = year === FORMAT_NUMERIC ? YEAR_NUMERIC_PLACEHOLDER : YEAR_2DIGIT_PLACEHOLDER;
  const monthToken = month === FORMAT_2DIGIT ? MONTH_2DIGIT_PLACEHOLDER : MONTH_NUMERIC_PLACEHOLDER;
  const dayToken = day === FORMAT_2DIGIT ? DAY_2DIGIT_PLACEHOLDER : DAY_NUMERIC_PLACEHOLDER;

  return `${dayToken}${separator}${monthToken}${separator}${yearToken}`;
}

export function externalToDate(v) {
  if (!v) return null;

  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : new Date(v.getFullYear(), v.getMonth(), v.getDate());
  }

  if (typeof v !== "string") return null;

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const parsed = new Date(v);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function dateToExternal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function buildTextMask(formatOptions = {}) {
  const sep = (formatOptions.separator || '.');
  const yearDigits = (formatOptions.year === 'numeric') ? 4 : 2;

  const mask = [/\d/, /\d/, sep, /\d/, /\d/, sep];
  for (let i = 0; i < yearDigits; i += 1) mask.push(/\d/);
  return mask;
}

export function isMaskedValueComplete(value, placeholderChar = '_') {
  const v = String(value || '');
  return v.length > 0 && !v.includes(placeholderChar);
}

export function isMaskedValueEmpty(value, placeholderChar = '_') {
  const v = String(value || '');
  return !/\d/.test(v.replace(new RegExp(placeholderChar, 'g'), ''));
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isValidMaskedDateString(value, formatOptions = {}, limits = {}) {
  const sep = formatOptions.separator || '.';
  const yearIsNumeric = formatOptions.year === 'numeric';

  const v = String(value || '').trim();
  if (!v) return false;
  if (v.includes('_')) return false;

  const re = yearIsNumeric
    ? new RegExp(`^(\\d{2})${escapeRegex(sep)}(\\d{2})${escapeRegex(sep)}(\\d{4})$`)
    : new RegExp(`^(\\d{2})${escapeRegex(sep)}(\\d{2})${escapeRegex(sep)}(\\d{2})$`);

  const m = v.match(re);
  if (!m) return false;

  const dd = Number(m[1]);
  const mm = Number(m[2]);

  if (!Number.isFinite(dd) || !Number.isFinite(mm)) return false;
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;

  let year;
  if (yearIsNumeric) {
    year = Number(m[3]);
  } else {
    const prefix = String(new Date().getFullYear()).slice(0, 2);
    year = Number(`${prefix}${m[3]}`);
  }
  if (!Number.isFinite(year)) return false;

  const d = new Date(year, mm - 1, dd);
  if (d.getFullYear() !== year) return false;
  if (d.getMonth() !== mm - 1) return false;
  if (d.getDate() !== dd) return false;

  const { min, max } = limits || {};
  if (min && d < min) return false;
  if (max && d > max) return false;

  return true;
}

export function buildRangeTextMask(formatOptions = {}, rangeSeparator = ' - ') {
  const sep = (formatOptions.separator || '.');
  const yearDigits = (formatOptions.year === 'numeric') ? 4 : 2;

  const dateMask = [/\d/, /\d/, sep, /\d/, /\d/, sep];
  for (let i = 0; i < yearDigits; i += 1) dateMask.push(/\d/);

  const rangeSepChars = String(rangeSeparator).split('');
  return [...dateMask, ...rangeSepChars, ...dateMask];
}

export function isMaskedRangeComplete(value, rangeSeparator = ' - ', placeholderChar = '_') {
  const v = String(value || '');
  return v.length > 0 && !v.includes(placeholderChar) && v.includes(rangeSeparator);
}

export function isMaskedRangeEmpty(value, placeholderChar = '_') {
  const v = String(value || '');
  return !/\d/.test(v.replace(new RegExp(placeholderChar, 'g'), ''));
}

export function splitMaskedRange(value, rangeSeparator = ' - ') {
  const v = String(value || '');
  const idx = v.indexOf(rangeSeparator);
  if (idx === -1) return [null, null];
  const a = v.slice(0, idx);
  const b = v.slice(idx + rangeSeparator.length);
  return [a, b];
}

export function isValidMaskedRangeString(value, formatOptions = {}, opts = {}) {
  const rangeSeparator = opts.rangeSeparator || ' - ';
  const endMax = opts.endMax || null;

  const [startStr, endStr] = splitMaskedRange(value, rangeSeparator);
  if (!startStr || !endStr) return false;

  const startOk = isValidMaskedDateString(startStr, formatOptions, { min: null, max: endMax });
  const endOk = isValidMaskedDateString(endStr, formatOptions, { min: null, max: endMax });
  if (!startOk || !endOk) return false;
  const sep = formatOptions.separator || '.';
  const startParts = startStr.split(sep);
  const endParts = endStr.split(sep);
  if (startParts.length !== 3 || endParts.length !== 3) return false;

  const yearIsNumeric = formatOptions.year === 'numeric';
  const toYear = (yy) => {
    if (yearIsNumeric) return Number(yy);
    const prefix = String(new Date().getFullYear()).slice(0, 2);
    return Number(`${prefix}${yy}`);
  };

  const startDate = new Date(toYear(startParts[2]), Number(startParts[1]) - 1, Number(startParts[0]));
  const endDate = new Date(toYear(endParts[2]), Number(endParts[1]) - 1, Number(endParts[0]));

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
  if (endDate < startDate) return false;

  return true;
}

export function findRangeSeparatorIndex(value) {
  const v = String(value || '');
  const tilde = v.indexOf('~');
  const dash = v.indexOf('-');

  if (tilde === -1) return dash;
  if (dash === -1) return tilde;
  return Math.min(tilde, dash);
}

export function getStartPart(value) {
  const v = String(value || '');
  const idx = findRangeSeparatorIndex(v);
  return idx === -1 ? v : v.slice(0, idx);
}

export function getEndPart(value) {
  const v = String(value || '');
  const idx = findRangeSeparatorIndex(v);
  return idx === -1 ? '' : v.slice(idx + 1);
}